import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import useStyles from "../CreativeRequest/styles";
import { THEME_COLORS } from "../../context/constants";

interface AddPlanModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (workspaceId: string, planData: any) => void;
  workspaces: any[];
}

export const AddPlanModal: React.FC<AddPlanModalProps> = ({
  open,
  onClose,
  onSubmit,
  workspaces,
}) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [planType, setPlanType] = useState("starter");
  const classes = useStyles();
  const [customData, setCustomData] = useState({
    pricePerUgc: 0,
    videoLimit: 0,
    seats: 10,
  });

  const handleSubmit = () => {
    if (!selectedWorkspace) return;

    if (planType === "custom") {
      onSubmit(selectedWorkspace._id, {
        planType,
        customPlanData: customData,
      });
    } else {
      onSubmit(selectedWorkspace._id, {
        planType,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Plan to Workspace</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", height: "400px" }}>
          {/* Workspace Selection Panel */}
          <Box sx={{ width: "40%", borderRight: "1px solid #eee", pr: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Workspace
            </Typography>
            <List dense>
              {workspaces.map((workspace) => (
                <ListItem
                  key={workspace._id}
                  disablePadding
                  sx={{
                    backgroundColor:
                      selectedWorkspace?._id === workspace._id
                        ? "action.selected"
                        : undefined,
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => setSelectedWorkspace(workspace)}
                  >
                    <ListItemText
                      primary={workspace.name.toUpperCase()}
                      secondary={
                        <Typography variant="body2" color={THEME_COLORS.yellow}>
                          {workspace.plan ? "has existing plan" : "no plan"}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Plan Selection Panel */}
          <Box sx={{ width: "60%", pl: 2 }}>
            {selectedWorkspace ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Plan for {selectedWorkspace.name.toUpperCase()}
                </Typography>

                <FormControl fullWidth margin="normal">
                  <InputLabel className={classes.inputLabel} id="planTypeLabel">
                    Plan Type
                  </InputLabel>
                  <Select
                    value={planType}
                    labelId="planTypeLabel"
                    label="Plan Type"
                    onChange={(e) => setPlanType(e.target.value)}
                  >
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="custom">Custom Plan</MenuItem>
                  </Select>
                </FormControl>

                {planType === "custom" && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Price per UGC"
                        type="number"
                        value={customData.pricePerUgc}
                        onChange={(e) =>
                          setCustomData({
                            ...customData,
                            pricePerUgc: parseFloat(e.target.value) || 0,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Video Limit"
                        type="number"
                        value={customData.videoLimit}
                        onChange={(e) =>
                          setCustomData({
                            ...customData,
                            videoLimit: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Seat Limit"
                        type="number"
                        value={customData.seats}
                        onChange={(e) =>
                          setCustomData({
                            ...customData,
                            seats: parseInt(e.target.value) || 10,
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Select a workspace to add a plan
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!selectedWorkspace}
        >
          Add Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};
